import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { MdFilterListOff, MdOutlinePictureAsPdf } from "react-icons/md";
import axios from "axios";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";
import Step3PreviewGenerate from "./Step3PreviewGenerate";

import { Link, useNavigate } from "react-router-dom";
import NavBar from "./Navbar";
import TopNavBar from "./TopNavbar";
import { Badge } from "./ui/badge";
import PaginationNew from "./PaginationNew";
import { SearchableSelect } from "./ui/searchable-select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Pencil, Trash2 } from "lucide-react";
import "../styles/Invoices.css";
import { parseLocalDate } from "../utils/parseLocalDate";
import { PERMISSIONS } from "../utils/rbac";
import {
  deniedActionClass,
  usePermissionGuard,
} from "../hooks/usePermissionGuard";

const apiUrl = process.env.REACT_APP_API_URL;

const safeToast = {
  success: (m) => {
    try {
      toast.success(m);
    } catch (e) {
      console.log(m);
    }
  },
  error: (m) => {
    try {
      toast.error(m);
    } catch (e) {
      console.log(m);
    }
  },
  message: (m) => {
    try {
      toast.message(m);
    } catch (e) {
      console.log(m);
    }
  },
};

function parseInvoiceDate(dateStr) {
  // "Oct 20, 2023" -> Date
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toDateOnlyStr(value) {
  if (!value) return "";
  const d = parseLocalDate(value);
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

function Invoices() {
  const navigate = useNavigate();
  const { guardAction, PermissionDialog, isAllowed } = usePermissionGuard();
  const canCreateInvoice = isAllowed(PERMISSIONS.CREATE_INVOICE);
  const canEditInvoice = isAllowed(PERMISSIONS.EDIT);
  const canDeleteInvoice = isAllowed(PERMISSIONS.DELETE);
  const canDownloadPdf = isAllowed(PERMISSIONS.GENERATE_PDF);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateRange, setDateRange] = useState("All Time");
  const [selectedProperty, setSelectedProperty] = useState("All Properties");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [allPropertyOptions, setAllPropertyOptions] = useState([
    { value: "All Properties", label: "All Properties" },
  ]);

  const [statsInvoices, setStatsInvoices] = useState([]);

  const pageSize = 10;

  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    dateRange !== "All Time" ||
    selectedProperty !== "All Properties" ||
    selectedStatus !== "All";

  const summary = useMemo(() => {
    const safe =
      (statsInvoices && statsInvoices.length ? statsInvoices : invoices) || [];
    const sentInvoices = safe.filter((i) => i.status === "SENT");
    const draftInvoices = safe.filter((i) => i.status === "DRAFT");

    const sentTotal = sentInvoices.reduce(
      (a, b) => a + Number(b.total_amount ?? b.subtotal ?? 0),
      0,
    );
    const draftTotal = draftInvoices.reduce(
      (a, b) => a + Number(b.total_amount ?? b.subtotal ?? 0),
      0,
    );

    return {
      sentTotal,
      sentCount: sentInvoices.length,
      draftTotal,
      draftCount: draftInvoices.length,
    };
  }, [invoices, statsInvoices]);

  const downloadInvoicePdf = async (invoiceId) => {
    if (!apiUrl) return;
    if (invoiceId == null || invoiceId === "") return;
    if (pdfGenerating) return;
    setPdfGenerating(true);

    let container = null;
    let root = null;

    try {
      safeToast.message("Generating PDF...");
      const res = await axios.get(`${apiUrl}/api/invoices/${invoiceId}`);
      const invoice = res?.data?.data?.invoice;
      const lines = Array.isArray(res?.data?.data?.lines)
        ? res.data.data.lines
        : [];
      if (!invoice) throw new Error("Invoice not found");

      const invNum = String(invoice.invoice_number || "")
        .replace("#", "")
        .trim();
      const filename = `invoice-${invNum || "draft"}.pdf`;

      container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "0";
      container.style.top = "0";
      container.style.width = "794px";
      container.style.minWidth = "794px";
      container.style.overflow = "visible";
      container.style.background = "white";
      container.style.opacity = "0";
      container.style.pointerEvents = "none";
      container.style.zIndex = "-1";

      const mount = document.createElement("div");
      container.appendChild(mount);
      document.body.appendChild(container);

      root = createRoot(mount);

      const step3Rows = (lines || []).map((l) => ({
        id:
          l.id ||
          `${l.employee_name || "row"}-${Math.random().toString(16).slice(2)}`,
        name: l.employee_name || l.name || "",
        role: l.role || "",
        hours: String(l.hours ?? ""),
        rate: String(l.rate ?? ""),
        isNew: false,
      }));

      root.render(
        <div style={{ padding: 32, backgroundColor: "#ffffff" }}>
          <Step3PreviewGenerate
            property={invoice.property_name || ""}
            invoiceNumber={invNum}
            invoiceDate={parseLocalDate(invoice.invoice_date)}
            startDate={parseLocalDate(invoice.start_date)}
            endDate={parseLocalDate(invoice.end_date)}
            dueDate={parseLocalDate(invoice.due_date)}
            rows={step3Rows}
            downloadPdf={false}
            setDownloadPdf={() => {}}
            emailClient={false}
            setEmailClient={() => {}}
            clientEmail={invoice.client_email || ""}
            setClientEmail={() => {}}
            errors={{}}
            onValidate={() => true}
            onGenerate={() => {}}
            onEmailTouched={() => {}}
            pdfFileName={filename}
            autoGeneratePdf={false}
            onPdfGenerated={() => {}}
            refPdfGenerate={() => {}}
          />
        </div>,
      );

      await new Promise((r) => requestAnimationFrame(() => r()));
      await new Promise((r) => setTimeout(r, 50));
      try {
        if (document?.fonts?.ready) await document.fonts.ready;
      } catch (e) {}

      const previewEl = container.querySelector(".invoice-preview-root");
      if (!previewEl) throw new Error("Preview not rendered");

      const contentToPrint = previewEl.cloneNode(true);
      const style = document.createElement("style");
      style.textContent = `
        .invoice-preview-root { background: white; }
        .invoice-preview-root * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .invoice-preview-rows { page-break-inside: auto; }
        .invoice-preview-row { break-inside: avoid; page-break-inside: avoid; }
      `;
      contentToPrint.prepend(style);

      const blob = await html2pdf()
        .from(contentToPrint)
        .set({
          margin: 10,
          filename,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 3,
            useCORS: true,
            backgroundColor: "#ffffff",
            windowWidth: 1200,
          },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .outputPdf("blob");

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = filename;
      link.click();
      safeToast.success("Downloaded successfully");
      container.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      safeToast.error("Failed to generate PDF");
    } finally {
      try {
        root?.unmount?.();
        container?.remove?.();
      } catch (e) {}
      setPdfGenerating(false);
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case "SENT":
        return "sent";
      case "DRAFT":
        return "draft";
      case "DELETED":
        return "deleted";
      default:
        return "draft";
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchProperties = async () => {
      if (!apiUrl) return;
      try {
        const res = await axios.get(`${apiUrl}/getPropertiesAndDeductions`);
        if (cancelled) return;
        const raw = Array.isArray(res?.data?.properties)
          ? res.data.properties
          : [];
        const opts = raw
          .map((p) => {
            const name = p?.NAME ?? p?.name;
            if (!name) return null;
            const v = String(name).trim();
            if (!v) return null;
            return { value: v, label: v };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label));
        setAllPropertyOptions([
          { value: "All Properties", label: "All Properties" },
          ...opts,
        ]);
      } catch (e) {
        // keep fallback
        setAllPropertyOptions([
          { value: "All Properties", label: "All Properties" },
        ]);
      }
    };
    fetchProperties();
    return () => {
      cancelled = true;
    };
  }, []);

  const statusOptions = useMemo(
    () => [
      { value: "All", label: "All" },
      { value: "SENT", label: "SENT" },
      // { value: "DELETED", label: "DELETED" },
      { value: "DRAFT", label: "DRAFT" },
    ],
    [],
  );

  const dateRangeOptions = useMemo(
    () => [
      { value: "All Time", label: "All Time" },
      { value: "Last 30 Days", label: "Last 30 Days" },
      { value: "Last 7 Days", label: "Last 7 Days" },
      { value: "Last 90 Days", label: "Last 90 Days" },
      { value: "This Month", label: "This Month" },
      { value: "Last Month", label: "Last Month" },
    ],
    [],
  );

  const referenceDate = useMemo(() => new Date(), []);

  const dateRangeWindow = useMemo(() => {
    const end = new Date(referenceDate);
    let start = null;

    if (dateRange === "All Time") {
      return { start: null, end: null };
    }

    if (dateRange === "Last 7 Days") {
      start = new Date(end);
      start.setDate(start.getDate() - 7);
    } else if (dateRange === "Last 30 Days") {
      start = new Date(end);
      start.setDate(start.getDate() - 30);
    } else if (dateRange === "Last 90 Days") {
      start = new Date(end);
      start.setDate(start.getDate() - 90);
    } else if (dateRange === "This Month") {
      start = startOfMonth(end);
      return { start, end: endOfMonth(end) };
    } else if (dateRange === "Last Month") {
      const prev = new Date(end.getFullYear(), end.getMonth() - 1, 1);
      return { start: startOfMonth(prev), end: endOfMonth(prev) };
    }

    return { start, end };
  }, [dateRange, referenceDate]);

  useEffect(() => {
    let cancelled = false;
    const fetchInvoices = async () => {
      if (!apiUrl) return;
      setLoading(true);
      setLoadError(null);
      try {
        const params = {
          page: currentPage,
          pageSize,
          status: selectedStatus === "All" ? "ALL" : selectedStatus,
          property: selectedProperty,
          q: searchQuery || "",
        };

        if (dateRangeWindow?.start)
          params.dateFrom = toDateOnlyStr(dateRangeWindow.start);
        if (dateRangeWindow?.end)
          params.dateTo = toDateOnlyStr(dateRangeWindow.end);

        const res = await axios.get(`${apiUrl}/api/invoices`, { params });
        if (cancelled) return;
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        setInvoices(rows);
        setTotalCount(res?.data?.totalCount || 0);
      } catch (e) {
        if (cancelled) return;
        setLoadError(e?.response?.data?.message || "Failed to load invoices");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchInvoices();
    return () => {
      cancelled = true;
    };
  }, [
    currentPage,
    dateRangeWindow?.end,
    dateRangeWindow?.start,
    searchQuery,
    selectedProperty,
    selectedStatus,
  ]);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      if (!apiUrl) return;

      try {
        const params = {
          page: 1,
          pageSize: 10000,
          status: selectedStatus === "All" ? "ALL" : selectedStatus,
          property: selectedProperty,
          q: searchQuery || "",
        };

        if (dateRangeWindow?.start)
          params.dateFrom = toDateOnlyStr(dateRangeWindow.start);
        if (dateRangeWindow?.end)
          params.dateTo = toDateOnlyStr(dateRangeWindow.end);

        const res = await axios.get(`${apiUrl}/api/invoices`, { params });
        if (cancelled) return;
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        setStatsInvoices(rows);
      } catch (e) {
        if (cancelled) return;
        setStatsInvoices([]);
      }
    };

    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [
    dateRangeWindow?.end,
    dateRangeWindow?.start,
    searchQuery,
    selectedProperty,
    selectedStatus,
  ]);

  // Reset to page 1 whenever filters/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, selectedProperty, selectedStatus, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentInvoices = invoices;

  const handleClearFilters = () => {
    setDateRange("All Time");
    setSelectedProperty("All Properties");
    setSelectedStatus("All");
    setSearchQuery("");
  };

  const deleteInvoice = async (invoiceId) => {
    if (!apiUrl) return;
    if (!invoiceId) return;
    if (deletingInvoiceId) return;
    setDeletingInvoiceId(invoiceId);
    try {
      await axios.delete(`${apiUrl}/api/invoices/${invoiceId}`);
      safeToast.success("Deleted successfully");
      setLoadError(null);
      setDeleteConfirmId(null);

      const targetPage = currentPage;

      const buildParams = (page) => {
        const params = {
          page,
          pageSize,
          status: selectedStatus === "All" ? "ALL" : selectedStatus,
          property: selectedProperty,
          q: searchQuery || "",
        };
        if (dateRangeWindow?.start)
          params.dateFrom = toDateOnlyStr(dateRangeWindow.start);
        if (dateRangeWindow?.end)
          params.dateTo = toDateOnlyStr(dateRangeWindow.end);
        return params;
      };

      const res = await axios.get(`${apiUrl}/api/invoices`, {
        params: buildParams(targetPage),
      });
      const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
      const nextTotal = res?.data?.totalCount || 0;

      if (!rows.length && targetPage > 1) {
        const prevPage = targetPage - 1;
        const resPrev = await axios.get(`${apiUrl}/api/invoices`, {
          params: buildParams(prevPage),
        });
        setInvoices(
          Array.isArray(resPrev?.data?.data) ? resPrev.data.data : [],
        );
        setTotalCount(resPrev?.data?.totalCount || 0);
        setCurrentPage(prevPage);
      } else {
        setInvoices(rows);
        setTotalCount(nextTotal);
      }

      try {
        const resStats = await axios.get(`${apiUrl}/api/invoices`, {
          params: buildParams(1),
        });
        setStatsInvoices(
          Array.isArray(resStats?.data?.data) ? resStats.data.data : [],
        );
      } catch (e) {
        setStatsInvoices([]);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to delete invoice";
      setLoadError(msg);
      safeToast.error(msg);
    } finally {
      setDeletingInvoiceId(null);
    }
  };

  return (
    <div className="admin-panel-parent-container">
      <PermissionDialog />

      <AlertDialog
        open={deleteConfirmId != null}
        onOpenChange={(open) => {
          if (!open && !deletingInvoiceId) setDeleteConfirmId(null);
        }}
      >
        <AlertDialogContent className="z-[9999]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black">
              Delete invoice?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-black">
              This action cannot be undone. This will permanently delete this
              invoice.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-black" disabled={!!deletingInvoiceId}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={`bg-red-600 hover:bg-red-500 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 outline-none focus:outline-none ${
                deletingInvoiceId ? "opacity-70 cursor-not-allowed" : ""
              }`}
              disabled={!!deletingInvoiceId}
              onClick={() => {
                if (deleteConfirmId != null) {
                  deleteInvoice(deleteConfirmId);
                }
              }}
            >
              {deletingInvoiceId ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NavBar />
      <TopNavBar />
      <div className="invoices-page-wrapper ml-[15%] mt-[66px] px-4 sm:px-6 lg:px-8 py-12 sm:py-6 bg-gray-100 min-h-[calc(100vh-66px)]">
        {/* Breadcrumbs */}
        <div className="text-sm mb-4">
          <span className="text-primary">Dashboard</span>
          <span className="text-primary mx-2">&gt;</span>
          <span className="text-primary font-bold">Invoices</span>
        </div>

        {/* Page Title */}
        <h1 className="text-3xl font-black text-[#131614] mb-6 ml-0">
          Invoices
        </h1>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-6">
          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border-[#2F7F570D] border-1 min-w-0">
            <div className="text-[11px] sm:text-xs font-semibold text-primary-50 uppercase tracking-wide mb-2">
              TOTAL EARNING
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-[#D97706] break-words">
              $
              {summary.sentTotal.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border-[#2F7F570D] border-1 min-w-0">
            <div className="text-[11px] sm:text-xs font-semibold text-primary-50 uppercase tracking-wide mb-2">
              SENT INVOICE
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-[#16A34A] break-words">
              {summary.sentCount} Invoices
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border-[#2F7F570D] border-1 min-w-0">
            <div className="text-[11px] sm:text-xs font-semibold text-primary-50 uppercase tracking-wide mb-2">
              DRAFT EARNING
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-primary break-words">
              $
              {summary.draftTotal.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border-[#2F7F570D] border-1 min-w-0">
            <div className="text-[11px] sm:text-xs font-semibold text-primary-50 uppercase tracking-wide mb-2">
              DRAFT INVOICE
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-[#9CA3AF] break-words">
              {summary.draftCount} Invoices
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-xl p-6 shadow-sm border-[#2F7F570D] border-1 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 items-stretch sm:items-center">
            <div className="flex-1 min-w-0 relative flex items-center">
              <svg
                className="absolute left-4 w-5 h-5 text-primary pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search by invoice #, client, or property"
                className="w-full min-w-0 py-2 px-4 pl-12 border border-[#2F7F571A] placeholder:text-[#2F7F574D] rounded-lg text-sm outline-none transition-colors focus:border-[#2F7F57] bg-[#F6F8F7] text-primary placeholder:text-primary"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button
              type="button"
              className={`flex w-auto items-center justify-center py-2 px-4 sm:px-6 bg-[#2F7F57] text-white rounded-lg font-semibold text-sm no-underline transition-colors whitespace-nowrap hover:bg-[#256845] border-none cursor-pointer ${deniedActionClass(canCreateInvoice)}`}
              onClick={() => {
                guardAction(
                  PERMISSIONS.CREATE_INVOICE,
                  "generate invoice",
                  () => {
                    navigate("/generateInvoiceNew");
                  },
                );
              }}
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Generate Invoice
            </button>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:gap-3 gap-3 items-stretch sm:items-center min-w-0">
              <SearchableSelect
                value={dateRange}
                onValueChange={setDateRange}
                options={dateRangeOptions}
                labelPrefix="Date Range:"
                className="w-full lg:min-w-[220px]"
              />

              <SearchableSelect
                value={selectedProperty}
                onValueChange={setSelectedProperty}
                options={allPropertyOptions}
                placeholder="All Properties"
                className="w-full lg:min-w-[220px]"
              />

              <SearchableSelect
                value={selectedStatus}
                onValueChange={setSelectedStatus}
                options={statusOptions}
                labelPrefix="Status:"
                className="w-full lg:min-w-[180px]"
              />
            </div>
            {hasActiveFilters ? (
              <button
                className="flex w-full lg:w-auto items-center justify-center py-2 lg:min-w-[160px] px-4 bg-transparent border-none text-primary text-sm cursor-pointer rounded-lg transition-colors hover:bg-gray-100"
                onClick={handleClearFilters}
              >
                <MdFilterListOff className="w-4 h-4 mr-1" />
                Clear Filters
              </button>
            ) : null}
          </div>
        </div>

        {/* Invoice Table */}
        <div className="bg-white rounded-xl shadow-sm mb-6 border border-[#2F7F571A] overflow-x-auto">
          <table className="w-full min-w-[860px] border-collapse">
            <thead>
              <tr>
                <th className="p-4 text-left text-xs font-semibold text-primary uppercase tracking-wide bg-[#2F7F570D] border-b border-[#2F7F571A]">
                  INVOICE #
                </th>
                <th className="p-4 text-left text-xs font-semibold text-primary uppercase tracking-wide bg-[#2F7F570D] border-b border-[#2F7F571A]">
                  PROPERTY
                </th>
                <th className="p-4 text-left text-xs font-semibold text-primary uppercase tracking-wide bg-[#2F7F570D] border-b border-[#2F7F571A]">
                  PAY PERIOD
                </th>
                <th className="p-4 text-left text-xs font-semibold text-primary uppercase tracking-wide bg-[#2F7F570D] border-b border-[#2F7F571A]">
                  DATE
                </th>
                <th className="p-4 text-left text-xs font-semibold text-primary uppercase tracking-wide bg-[#2F7F570D] border-b border-[#2F7F571A]">
                  TOTAL AMOUNT
                </th>
                <th className="p-4 text-left text-xs font-semibold text-primary uppercase tracking-wide bg-[#2F7F570D] border-b border-[#2F7F571A]">
                  STATUS
                </th>
                <th className="p-4 text-left text-xs font-semibold text-primary uppercase tracking-wide bg-[#2F7F570D] border-b border-[#2F7F571A]">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="p-4 text-sm text-primary" colSpan={7}>
                    Loading...
                  </td>
                </tr>
              ) : loadError ? (
                <tr>
                  <td className="p-4 text-sm text-red-600" colSpan={7}>
                    {loadError}
                  </td>
                </tr>
              ) : currentInvoices.length === 0 ? (
                <tr>
                  <td className="p-4 text-sm text-primary" colSpan={7}>
                    No invoices found
                  </td>
                </tr>
              ) : null}
              {currentInvoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  className="border-b border-[#2F7F571A] transition-colors hover:bg-gray-50 last:border-b-0"
                  onClick={() => {
                    const id = invoice.id;
                    const status = String(invoice.status || "").toUpperCase();
                    if (!canEditInvoice) {
                      navigate(
                        `/generateInvoiceNew?invoiceId=${encodeURIComponent(id)}&forceStep=3`,
                      );
                      return;
                    }
                    if (status === "DRAFT") {
                      navigate(
                        `/generateInvoiceNew?invoiceId=${encodeURIComponent(id)}`,
                      );
                      return;
                    }
                    navigate(
                      `/generateInvoiceNew?invoiceId=${encodeURIComponent(id)}&forceStep=3`,
                    );
                  }}
                >
                  <td className="p-4 text-sm text-primary">
                    <span className="text-[#2F7F57] font-semibold">
                      #{invoice.invoice_number}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-[#131614]">
                    {invoice.property_name}
                  </td>
                  <td className="p-4 text-sm text-primary">
                    {toDateOnlyStr(invoice.start_date)} -{" "}
                    {toDateOnlyStr(invoice.end_date)}
                  </td>
                  <td className="p-4 text-sm text-primary">
                    {toDateOnlyStr(invoice.invoice_date)}
                  </td>
                  <td className="p-4 text-sm font-bold text-[#131614]">
                    ${Number(invoice.subtotal || 0).toFixed(2)}
                  </td>
                  <td className="p-4 text-sm">
                    <Badge variant={getStatusVariant(invoice.status)}>
                      {invoice.status}
                    </Badge>
                  </td>
                  <td className="p-4 text-sm">
                    <div className="flex gap-2 items-center">
                    <button
                      className="p-1.5 bg-transparent border-none text-primary cursor-pointer rounded transition-all flex items-center justify-center hover:bg-gray-100 hover:text-primary"
                      title="View"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/generateInvoiceNew?invoiceId=${encodeURIComponent(invoice.id)}&forceStep=3`,
                        );
                      }}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>
                    <button
                      className={`p-1.5 bg-transparent border-none text-primary cursor-pointer rounded transition-all flex items-center justify-center hover:bg-gray-100 hover:text-primary ${pdfGenerating ? "opacity-60 cursor-not-allowed" : deniedActionClass(canDownloadPdf)}`}
                      title="Download PDF"
                      type="button"
                      disabled={pdfGenerating}
                      onClick={(e) => {
                        e.stopPropagation();
                        guardAction(
                          PERMISSIONS.GENERATE_PDF,
                          "generate PDF",
                          () => {
                            downloadInvoicePdf(invoice.id);
                          },
                        );
                      }}
                    >
                      {pdfGenerating ? (
                        <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      ) : (
                        <MdOutlinePictureAsPdf className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      className="p-1.5 bg-transparent border-none text-primary cursor-pointer rounded transition-all flex items-center justify-center hover:bg-gray-100 hover:text-primary"
                      title="More"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="p-1.5 bg-transparent border-none text-primary cursor-pointer rounded transition-all flex items-center justify-center hover:bg-gray-100 hover:text-primary"
                            title="More"
                            type="button"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                              />
                            </svg>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              className={`flex items-center gap-2 no-underline text-primary hover:no-underline ${deniedActionClass(canEditInvoice)}`}
                              onSelect={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                guardAction(
                                  PERMISSIONS.EDIT,
                                  "edit invoice",
                                  () => {
                                    navigate(
                                      `/generateInvoiceNew?invoiceId=${encodeURIComponent(
                                        String(invoice.id),
                                      )}`,
                                    );
                                  },
                                );
                              }}
                            >
                              <Pencil className="h-4 w-4 text-primary" />
                              <span className="text-primary">Edit</span>
                            </DropdownMenuItem>

                            <DropdownMenuItem
                              onSelect={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                guardAction(
                                  PERMISSIONS.DELETE,
                                  "delete invoice",
                                  () => {
                                    setDeleteConfirmId(invoice.id);
                                  },
                                );
                              }}
                              className={`flex items-center gap-2 text-red-600 focus:text-red-600 outline-none focus:outline-none ${deniedActionClass(canDeleteInvoice)}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                              <span>Delete</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                      </DropdownMenu>
                    </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 py-4">
          <div className="text-sm text-primary">
            Showing {startIndex + 1} to {Math.min(endIndex, totalCount)} of{" "}
            {totalCount} entries
          </div>
          <PaginationNew
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
}

export default Invoices;
